import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { testimonials } from "@/data/testimonials";

const GOOGLE_REVIEW_URL = "https://g.page/r/CY0tN6Q3NwxJEB0/review";
const OVERALL_RATING = 4.9;
const TOTAL_REVIEWS = 127;

const GoogleGLogo = ({ className = "h-6 w-6" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#4285F4" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#34A853" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#EA4335" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

const renderStars = (rating: number, size = "h-4 w-4") => {
  const full = Math.floor(rating);
  const hasHalf = rating - full >= 0.5;
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(5)].map((_, i) => {
        const isFull = i < full;
        const isHalf = i === full && hasHalf;
        return (
          <Star
            key={i}
            className={`${size} ${isFull || isHalf ? "fill-[#FBBC05] text-[#FBBC05]" : "fill-muted text-muted-foreground/30"}`}
          />
        );
      })}
    </div>
  );
};

const formatRelativeDate = (index: number) => {
  const options = ["a week ago", "2 weeks ago", "a month ago", "2 months ago", "3 months ago", "4 months ago", "5 months ago", "6 months ago"];
  return options[index % options.length];
};

export const GoogleReviewsSection = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScrollButtons = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 4);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 4);
    }
  };

  useEffect(() => {
    checkScrollButtons();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", checkScrollButtons, { passive: true });
    window.addEventListener("resize", checkScrollButtons);
    return () => {
      el.removeEventListener("scroll", checkScrollButtons);
      window.removeEventListener("resize", checkScrollButtons);
    };
  }, []);

  const scroll = (direction: "left" | "right") => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: direction === "left" ? -360 : 360, behavior: "smooth" });
    }
  };

  return (
    <section className="section-padding bg-muted/30" aria-labelledby="google-reviews-heading">
      <div className="container-luxury">
        <div className="text-center space-y-4 mb-10">
          <span className="section-badge">Google Reviews</span>
          <h2 id="google-reviews-heading" className="section-title">
            Loved by Travelers Across India
          </h2>
          <p className="section-subtitle">
            Genuine reviews from happy customers who trusted us with their journeys.
          </p>
        </div>

        {/* Rating Summary Card */}
        <div className="max-w-2xl mx-auto mb-12">
          <div className="luxury-card bg-background p-6 md:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <GoogleGLogo className="h-10 w-10 md:h-12 md:w-12" />
              <div>
                <p className="font-heading font-bold text-foreground text-lg md:text-xl leading-tight">
                  Google Reviews
                </p>
                <p className="text-xs text-muted-foreground">Verified customer feedback</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-center">
                <p className="font-heading font-bold text-3xl md:text-4xl text-foreground leading-none">
                  {OVERALL_RATING}
                </p>
                <div className="mt-1.5">{renderStars(OVERALL_RATING, "h-4 w-4")}</div>
                <p className="text-xs text-muted-foreground mt-1">{TOTAL_REVIEWS} reviews</p>
              </div>
              <Button asChild size="sm" className="rounded-full bg-[#4285F4] hover:bg-[#3367D6] text-white shadow-soft">
                <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer">
                  Write a Review
                </a>
              </Button>
            </div>
          </div>
        </div>

        {/* Reviews Carousel */}
        <div className="relative">
          <Button
            variant="outline"
            size="icon"
            onClick={() => scroll("left")}
            disabled={!canScrollLeft}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 rounded-full bg-background shadow-medium hidden md:flex disabled:opacity-0 h-10 w-10"
            aria-label="Scroll reviews left"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>

          <div
            ref={scrollRef}
            className="flex gap-5 overflow-x-auto pb-4 px-1 snap-x snap-mandatory scrollbar-hide md:px-14"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {testimonials.map((t, idx) => (
              <article
                key={t.id}
                className="flex-shrink-0 w-[300px] md:w-[340px] snap-start"
              >
                <div className="bg-background rounded-2xl border border-border p-6 h-full flex flex-col gap-4 shadow-soft hover:shadow-medium transition-shadow">
                  {/* Header: avatar + name + Google logo */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-11 h-11 rounded-full overflow-hidden bg-muted flex-shrink-0">
                        <img
                          src={t.image}
                          alt={t.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-sm text-foreground truncate">{t.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{t.location}</p>
                      </div>
                    </div>
                    <GoogleGLogo className="h-5 w-5 flex-shrink-0 mt-1" />
                  </div>

                  {/* Stars + date */}
                  <div className="flex items-center gap-2">
                    {renderStars(t.rating)}
                    <span className="text-xs text-muted-foreground">{formatRelativeDate(idx)}</span>
                  </div>

                  {/* Review body */}
                  <p className="text-sm text-foreground/80 leading-relaxed line-clamp-5 flex-1">
                    {t.feedback}
                  </p>

                  {/* Trip badge */}
                  <div className="pt-2 border-t border-border">
                    <span className="inline-block text-xs text-muted-foreground">
                      Trip: <span className="font-medium text-foreground">{t.destination}</span>
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <Button
            variant="outline"
            size="icon"
            onClick={() => scroll("right")}
            disabled={!canScrollRight}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 rounded-full bg-background shadow-medium hidden md:flex disabled:opacity-0 h-10 w-10"
            aria-label="Scroll reviews right"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>

        {/* Footer CTA */}
        <div className="text-center mt-10">
          <a
            href={GOOGLE_REVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
          >
            See all reviews on Google
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-xs text-muted-foreground mt-6 md:hidden">Swipe to see more reviews →</p>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviewsSection;